import React, { ReactNode, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Form, Input, InputNumber, Popconfirm, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { QuestionCircleOutlined, SaveOutlined, StopOutlined } from '@ant-design/icons';
import { EditButton, RemoveButton } from '../../CustomButtons';
import { CardLayout, cardLayoutOptions, viewOption } from '../../Layout';
import { RateUnitSelect } from './RateUnitSelect';
import { customFilterIcon } from './customFilterIcon';
import cx from 'classnames';
import { FieldData } from 'rc-field-form/es/interface';
import { IIncludeAssignedCompanyItem, IRateListRespEntryV2, IShowComponent, RareFormTab } from '../../../interfaces';
import {
    useOperatorsSelect,
    useRateEditCountriesByZoneSelectV2,
    useRateEditNetworksByCountryZoneSelectV2,
    useRateInfo,
    useRateListCountryFilterList,
    useRateListNetworkFilterList,
    useRateListOperatorFilterList,
    useRateListV2,
    useRateListZoneFilterList,
    useUiIsSpinnerFound,
    useZonesSelect,
} from '../../../hooks';
import {
    addRateChangedFormTab,
    addRateListStoreItemV2,
    apiMessagesDeleteMessageFormFieldName,
    apiMessagesGetFormRules,
    deleteRateEntriesV2,
    deleteRateListStoreItemV2,
    getCountryIdByNetworksIt,
    getZoneIdByCountryIt,
    removeRateChangedFormTab,
    saveRateEntriesV2,
    setRateEditSelectedCountryIdV2,
    setRateEditSelectedZoneIdV2,
    showValueLabel,
} from '../../../middleware';
import { isArrayHasNewId, isNewId } from '../../../instruments';
import { tB, tF, tNM } from '../../../translate';
import { Catcher, FormSelect, useSkyTablePagination } from '../../../Components';
import IconStyles from '../../IconComponents/m_styles.less';
import ViewStyles from '../../styles/m_viewStyles.less';

interface IDataType {
    key: string;
    id: string;
    country?: ReactNode;
    countryId?: string;
    incrementData?: ReactNode;
    network?: ReactNode;
    networkId?: string;
    operator?: ReactNode;
    operatorId?: string;
    rateData?: ReactNode;
    rateDataUnitId?: string;
    rateSms?: ReactNode;
    rateSmsUnitId?: string;
    rateVoiceMobile?: ReactNode;
    rateVoiceNet?: ReactNode;
    rateVoiceNetUnitId?: string;
    zone?: ReactNode;
    zoneId?: string;
    toolBar?: ReactNode;
}

export const EditRatesV2: React.FC<IShowComponent> = observer(({ loadingTypes }: IShowComponent) => {
    const [editId, setEditId] = useState<string | undefined>();
    const [isValuesChange, setIsValuesChange] = useState<boolean>(false);
    const [form] = Form.useForm();

    const isLoading = !!loadingTypes && !!loadingTypes.length && useUiIsSpinnerFound(loadingTypes);
    const { currency } = useRateInfo();
    const list = useRateListV2();
    const operatorFilterList = useRateListOperatorFilterList();
    const zoneFilterList = useRateListZoneFilterList();
    const countryFilterList = useRateListCountryFilterList();
    const networkFilterList = useRateListNetworkFilterList();
    const skyTablePagination = useSkyTablePagination({ total: list.length });

    const isHasNew = isArrayHasNewId(list);

    const columns: ColumnsType<IDataType> = [
        {
            title: tF('Operator'),
            dataIndex: 'operator',
            width: viewOption.rateTableEdit.widthNetwork,
            filters: operatorFilterList.length > 1 ? operatorFilterList : undefined,
            filterIcon: customFilterIcon,
            filterSearch: true,
            onFilter: (value: string | number | boolean, record: IDataType): boolean =>
                editId === record.id ||
                record.operatorId === value ||
                (value === 'default' && typeof record.operatorId === 'undefined'),
            sorter: {
                compare: (a: any, b: any) =>
                    (a.operatorName || '') < (b.operatorName || '')
                        ? -1
                        : (a.operatorName || '') > (b.operatorName || '')
                        ? 1
                        : 0,
                multiple: 1,
            },
        },
        {
            title: tF('Zone'),
            dataIndex: 'zone',
            width: viewOption.rateTableEdit.widthNetwork,
            filters: zoneFilterList.length > 1 ? zoneFilterList : undefined,
            filterIcon: customFilterIcon,
            filterSearch: true,
            onFilter: (value: string | number | boolean, record: IDataType): boolean => {
                return editId === record.id || record.zoneId === value;
            },
            sorter: {
                compare: (a: any, b: any) =>
                    (a.zoneName || '') < (b.zoneName || '') ? -1 : (a.zoneName || '') > (b.zoneName || '') ? 1 : 0,
                multiple: 2,
            },
        },
        {
            title: tF('Country'),
            dataIndex: 'country',
            width: viewOption.rateTableEdit.widthNetwork,
            filters: countryFilterList.length > 1 ? countryFilterList : undefined,
            filterIcon: customFilterIcon,
            filterSearch: true,
            onFilter: (value: string | number | boolean, record: IDataType): boolean => {
                return editId === record.id || record.countryId === value;
            },
            sorter: {
                compare: (a: any, b: any) =>
                    (a.countryName || '') < (b.countryName || '')
                        ? -1
                        : (a.countryName || '') > (b.countryName || '')
                        ? 1
                        : 0,
                multiple: 3,
            },
        },
        {
            title: tF('Network'),
            dataIndex: 'network',
            width: viewOption.rateTableEdit.widthNetwork + viewOption.networkOffset,
            filters: networkFilterList.length > 1 ? networkFilterList : undefined,
            filterIcon: customFilterIcon,
            filterSearch: true,
            onFilter: (value: string | number | boolean, record: IDataType): boolean => {
                return editId === record.id || record.networkId === value;
            },
            sorter: {
                compare: (a: any, b: any) =>
                    (a.networkName || '') < (b.networkName || '')
                        ? -1
                        : (a.networkName || '') > (b.networkName || '')
                        ? 1
                        : 0,
                multiple: 4,
            },
        },
        {
            title: 'Increment Data',
            dataIndex: 'incrementData',
            className: cx(ViewStyles.columnRight),
            width: viewOption.rateTableEdit.widthValue,
        },
        {
            title: tF('Gigabyte'),
            dataIndex: 'rateData',
            className: cx(ViewStyles.columnRight),
            width: viewOption.rateTableEdit.widthValue,
        },
        {
            title: tF('SMS'),
            dataIndex: 'rateSms',
            className: cx(ViewStyles.columnRight),
            width: viewOption.rateTableEdit.widthValue,
        },
        {
            title: tF('Voice Mobile'),
            dataIndex: 'rateVoiceMobile',
            className: cx(ViewStyles.columnRight),
            width: viewOption.rateTableEdit.widthValue,
        },
        {
            title: tF('Voice Fix Net'),
            dataIndex: 'rateVoiceNet',
            className: cx(ViewStyles.columnRight),
            width: viewOption.rateTableEdit.widthValue,
        },
        {
            title: '',
            dataIndex: 'toolBar',
            className: cx(ViewStyles.columnShortToolbar),
            width: viewOption.rateTableEdit.widthToolBarV2,
        },
    ];

    useEffect(() => {
        const currentItem = list.find((i: IIncludeAssignedCompanyItem) => i.id === editId);
        form.setFieldValue('operatorId', currentItem?.operatorId);
        form.setFieldValue('zoneId', currentItem?.zoneId);
        form.setFieldValue('countryId', currentItem?.countryId);
        form.setFieldValue('networkId', currentItem?.networkId);
        form.setFieldValue('incrementData', currentItem?.incrementData);
        form.setFieldValue('rateData', currentItem?.rateData);
        form.setFieldValue('rateDataUnitId', currentItem?.rateDataUnitId);
        form.setFieldValue('rateSms', currentItem?.rateSms);
        form.setFieldValue('rateSmsUnitId', currentItem?.rateSmsUnitId);
        form.setFieldValue('rateVoiceMobile', currentItem?.rateVoiceMobile);
        form.setFieldValue('rateVoiceMobileUnitId', currentItem?.rateVoiceMobileUnitId);
        form.setFieldValue('rateVoiceNet', currentItem?.rateVoiceNet);
        form.setFieldValue('rateVoiceNetUnitId', currentItem?.rateVoiceNetUnitId);

        setRateEditSelectedZoneIdV2(currentItem?.zoneId);
        setRateEditSelectedCountryIdV2(currentItem?.countryId);

        if (!editId) {
            removeRateChangedFormTab(RareFormTab.RATES);
        }
    }, [editId]);

    const selectedNetwork = (id: string) => {
        if (id) {
            const countryId = getCountryIdByNetworksIt(id);
            form.setFieldValue('countryId', countryId);
            setRateEditSelectedCountryIdV2(countryId);
            if (countryId) {
                const zoneId = getZoneIdByCountryIt(countryId);
                form.setFieldValue('zoneId', zoneId);
                setRateEditSelectedZoneIdV2(zoneId);
            }
        }
    };
    const selectedCountry = (id: string) => {
        form.setFieldValue('networkId', undefined);
        if (id) {
            const zoneId = getZoneIdByCountryIt(id);
            form.setFieldValue('zoneId', zoneId);
        }
        setRateEditSelectedCountryIdV2(id);
    };
    const selectedZone = (id: string) => {
        form.setFieldValue('countryId', undefined);
        form.setFieldValue('networkId', undefined);
        setRateEditSelectedZoneIdV2(id);
        setRateEditSelectedCountryIdV2(undefined);
    };

    const checkValid = (): void => {
        form.validateFields();
    };
    const addRate = () => {
        const newId = addRateListStoreItemV2();
        setEditId(newId);
    };

    const startEditRate = (id: string) => {
        if (editId && isNewId(editId)) {
            deleteRateListStoreItemV2(editId);
        }
        setEditId(id);
        setIsValuesChange(false);
        removeRateChangedFormTab(RareFormTab.RATES);
    };

    const deleteRate = async (itemId: string) => {
        await deleteRateEntriesV2(itemId);
    };

    const saveRate = async (id: string) => {
        const values = form.getFieldsValue();
        const isSaved = await saveRateEntriesV2(id, values);
        if (isSaved) {
            setEditId(undefined);
        } else {
            checkValid();
        }
    };

    const canselEditRate = (id: string) => {
        if (isNewId(id)) {
            deleteRateListStoreItemV2(id);
        }

        setEditId(undefined);
    };

    const onValuesChange = (changedFields: FieldData[], values: any) => {
        Object.keys(changedFields).forEach((key) => {
            apiMessagesDeleteMessageFormFieldName(key);
            const changedValue = values[key];
            switch (key) {
                case 'zoneId':
                    selectedZone(changedValue);
                    break;
                case 'countryId':
                    selectedCountry(changedValue);
                    break;
                case 'networkId':
                    selectedNetwork(changedValue);
                    break;
            }
        });
        setIsValuesChange(true);
        addRateChangedFormTab(RareFormTab.RATES);
    };

    const dataSource: IDataType[] = list.map((i: IRateListRespEntryV2) => ({
        key: i.id,
        ...i,
        operator:
            editId === i.id ? (
                <div className={ViewStyles.tableFieldMarginTop}>
                    <FormSelect
                        disabled={isLoading}
                        fieldName={'operatorId'}
                        isGetPopupContainerEnabled={false}
                        isShortForm
                        rules={apiMessagesGetFormRules}
                        useSelectHook={useOperatorsSelect}
                    />
                </div>
            ) : (
                i.operatorName || i.operatorId || 'Default'
            ),
        zone:
            editId === i.id ? (
                <FormSelect
                    disabled={isLoading}
                    fieldName={'zoneId'}
                    isGetPopupContainerEnabled={false}
                    isShortForm
                    rules={apiMessagesGetFormRules}
                    useSelectHook={useZonesSelect}
                />
            ) : (
                i.zoneName || i.zoneId
            ),
        country:
            editId === i.id ? (
                <FormSelect
                    disabled={isLoading}
                    fieldName={'countryId'}
                    isGetPopupContainerEnabled={false}
                    isShortForm
                    rules={apiMessagesGetFormRules}
                    useSelectHook={useRateEditCountriesByZoneSelectV2}
                />
            ) : (
                i.countryName || i.countryId
            ),
        network:
            editId === i.id ? (
                <FormSelect
                    disabled={isLoading}
                    fieldName={'networkId'}
                    isGetPopupContainerEnabled={false}
                    isShortForm
                    rules={apiMessagesGetFormRules}
                    useSelectHook={useRateEditNetworksByCountryZoneSelectV2}
                />
            ) : (
                i.networkName || i.networkId
            ),
        incrementData:
            editId === i.id ? (
                <Form.Item name={'incrementData'} className={ViewStyles.tableFieldAlign}>
                    <InputNumber min={0.1} step={0.1} style={{ width: '100%' }} />
                </Form.Item>
            ) : (
                i.incrementData ?? '—'
            ),
        rateData:
            editId === i.id ? (
                <Form.Item name={'rateData'} rules={apiMessagesGetFormRules} className={ViewStyles.tableFieldAlign}>
                    <Input
                        addonBefore={<RateUnitSelect isV2 name={'rateDataUnitId'} rules={apiMessagesGetFormRules} />}
                    />
                </Form.Item>
            ) : (
                showValueLabel({ currency, unit: i.rateDataUnitId, value: i.rateData })
            ),
        rateSms:
            editId === i.id ? (
                <Form.Item name={'rateSms'} rules={apiMessagesGetFormRules} className={ViewStyles.tableFieldAlign}>
                    <Input
                        addonBefore={<RateUnitSelect isV2 name={'rateSmsUnitId'} rules={apiMessagesGetFormRules} />}
                    />
                </Form.Item>
            ) : (
                showValueLabel({ currency, unit: i.rateSmsUnitId, value: i.rateSms })
            ),
        rateVoiceMobile:
            editId === i.id ? (
                <Form.Item
                    name={'rateVoiceMobile'}
                    rules={apiMessagesGetFormRules}
                    className={ViewStyles.tableFieldAlign}
                >
                    <Input
                        addonBefore={
                            <RateUnitSelect isV2 name={'rateVoiceMobileUnitId'} rules={apiMessagesGetFormRules} />
                        }
                    />
                </Form.Item>
            ) : (
                showValueLabel({ currency, unit: i.rateVoiceMobileUnitId, value: i.rateVoiceMobile })
            ),
        rateVoiceNet:
            editId === i.id ? (
                <Form.Item name={'rateVoiceNet'} rules={apiMessagesGetFormRules} className={ViewStyles.tableFieldAlign}>
                    <Input
                        addonBefore={
                            <RateUnitSelect isV2 name={'rateVoiceNetUnitId'} rules={apiMessagesGetFormRules} />
                        }
                    />
                </Form.Item>
            ) : (
                showValueLabel({ currency, unit: i.rateVoiceNetUnitId, value: i.rateVoiceNet })
            ),
        toolBar:
            editId === i.id ? (
                <div className={cx(ViewStyles.columnShortToolbar, ViewStyles.tableFieldAlign)}>
                    <Button
                        disabled={isLoading || !isValuesChange}
                        icon={
                            <SaveOutlined
                                className={cx(IconStyles.buttonIcon, {
                                    [IconStyles.disabled]: isLoading || !isValuesChange,
                                })}
                            />
                        }
                        onClick={() => saveRate(i.id)}
                        title={'Save'}
                        type={'primary'}
                    />
                    <Button
                        disabled={isLoading}
                        icon={<StopOutlined className={cx(IconStyles.buttonIcon, IconStyles.done)} />}
                        onClick={() => canselEditRate(i.id)}
                        title={'Cancel'}
                    />
                </div>
            ) : (
                <div className={cx(ViewStyles.columnShortToolbar, ViewStyles.tableFieldAlign)}>
                    <EditButton
                        disabled={isLoading}
                        isHiddenTitle
                        onClick={(): void => {
                            startEditRate(i.id);
                        }}
                    />
                    <Popconfirm
                        cancelText={tB('Cancel')}
                        okText={tB('Delete')}
                        onConfirm={() => deleteRate(i.id)}
                        placement="bottom"
                        title={tNM('Confirm deletion.')}
                        icon={<QuestionCircleOutlined className={IconStyles.canceled} />}
                    >
                        <RemoveButton disabled={isLoading} isHiddenTitle isGhost />
                    </Popconfirm>
                </div>
            ),
    }));

    return (
        <Catcher>
            <CardLayout
                columnOption={cardLayoutOptions.cardTableFull}
                count={list.length}
                idSpinners={loadingTypes}
                isTable
                onClickAddButton={isHasNew ? undefined : addRate}
                title={'Rates'}
            >
                <Form
                    form={form}
                    name="rate"
                    initialValues={{}}
                    scrollToFirstError={true}
                    layout={'vertical'}
                    className={'formStyle'}
                    onValuesChange={onValuesChange}
                >
                    <Table
                        className={cx('sub-table-rate')}
                        columns={columns}
                        dataSource={dataSource}
                        loading={isLoading}
                        size={'small'}
                        pagination={skyTablePagination}
                    />
                </Form>
            </CardLayout>
        </Catcher>
    );
});
